import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { industries, capabilities, useCases } from "../../data/mock";
import {
  ArrowRight,
  Dices,
  BatteryCharging,
  Radar,
  CheckCircle2,
  FileCode2,
  Boxes,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { Reveal } from "../../components/animations/Reveal";
import { cn } from "@/lib/utils";

const industryIcons = { Dices, BatteryCharging, Radar };
const capabilityIcons = { FileCode2, Boxes, ShieldCheck, Workflow };

const accentClasses = {
  igaming: {
    text: "text-[hsl(var(--igaming))]",
    bg: "bg-[hsl(var(--igaming))]/10",
    border: "border-[hsl(var(--igaming))]/30",
    bar: "from-[hsl(var(--igaming))] to-[hsl(var(--igaming-2))]",
    ring: "ring-[hsl(var(--igaming))]/30",
  },
  battery: {
    text: "text-[hsl(var(--battery))]",
    bg: "bg-[hsl(var(--battery))]/10",
    border: "border-[hsl(var(--battery))]/30",
    bar: "from-[hsl(var(--battery))] to-[hsl(var(--battery-2))]",
    ring: "ring-[hsl(var(--battery))]/30",
  },
  automotive: {
    text: "text-[hsl(var(--automotive))]",
    bg: "bg-[hsl(var(--automotive))]/10",
    border: "border-[hsl(var(--automotive))]/30",
    bar: "from-[hsl(var(--automotive))] to-[hsl(var(--automotive-2))]",
    ring: "ring-[hsl(var(--automotive))]/30",
  },
};

// --- Industry-specific motion motifs ------------------------------------

const IGamingMotif = () => {
  const odds = ["1.85", "2.40", "3.10", "1.55", "4.20", "2.05", "1.92", "3.75"];
  const row = [...odds, ...odds];
  return (
    <div className="relative overflow-hidden rounded-xl border border-[hsl(var(--igaming))]/20 bg-[hsl(var(--igaming))]/5 py-4">
      <div className="flex gap-6 ticker-track w-max font-mono text-sm">
        {row.map((o, i) => (
          <span key={i} className="flex items-center gap-2 text-[hsl(var(--igaming))] px-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--igaming))] animate-pulse" />
            ODDS {o}
          </span>
        ))}
      </div>
    </div>
  );
};

const BatteryMotif = () => {
  const cells = Array.from({ length: 24 });
  return (
    <div className="rounded-xl border border-[hsl(var(--battery))]/20 bg-[hsl(var(--battery))]/5 p-5">
      <div className="grid grid-cols-8 gap-2">
        {cells.map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-sm bg-[hsl(var(--battery))] cell-pulse"
            style={{ animationDelay: `${(i % 8) * 0.18}s` }}
          />
        ))}
      </div>
    </div>
  );
};

const AutomotiveMotif = () => (
  <div className="relative rounded-xl border border-[hsl(var(--automotive))]/20 bg-[hsl(var(--automotive))]/5 p-8 flex items-center justify-center overflow-hidden h-40">
    <div className="relative w-28 h-28">
      <div className="absolute inset-0 rounded-full border border-[hsl(var(--automotive))]/30" />
      <div className="absolute inset-4 rounded-full border border-[hsl(var(--automotive))]/30" />
      <div className="absolute inset-8 rounded-full border border-[hsl(var(--automotive))]/30" />
      <div className="absolute inset-0 radar-sweep" style={{ transformOrigin: "50% 50%" }}>
        <div
          className="absolute top-1/2 left-1/2 w-1/2 h-0.5 origin-left"
          style={{ background: "linear-gradient(90deg, hsl(var(--automotive)), transparent)" }}
        />
      </div>
      <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 rounded-full bg-[hsl(var(--automotive))]" />
      <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 rounded-full bg-[hsl(var(--automotive))]/60 radar-ping" />
    </div>
  </div>
);

const motifs = { igaming: IGamingMotif, battery: BatteryMotif, automotive: AutomotiveMotif };

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = industries.find((i) => i.slug === slug);

  if (!industry) return <Navigate to="/industries" replace />;

  const accent = accentClasses[industry.accent];
  const Icon = industryIcons[industry.icon] || Dices;
  const Motif = motifs[industry.accent];
  const relatedUseCases = useCases.filter((u) => u.industry === industry.slug);
  const relatedCaps = capabilities.filter((c) => industry.relatedCapabilities.includes(c.slug));

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 graph-bg" />
        <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", accent.bar)} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-mono uppercase tracking-wider mb-6", accent.bg, accent.border, accent.text)}>
                <Icon className="w-4 h-4" />
                {industry.fullName}
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                {industry.tagline}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {industry.dataChallenge}
              </p>
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12">
                  Discuss Your {industry.name} Data
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <Reveal>{Motif && <Motif />}</Reveal>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="py-16 lg:py-20 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-xl lg:text-2xl text-foreground font-light leading-relaxed">
              "{industry.positioning}"
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pipeline */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className={cn("text-sm font-mono uppercase tracking-wider mb-3", accent.text)}>Reference Pipeline</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">How the Data Actually Moves</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {industry.pipeline.map((p, i) => (
              <Reveal key={p.step} delay={i * 90} className="relative p-6 rounded-2xl bg-card border border-border">
                <span className={cn("font-mono text-xs", accent.text)}>{String(i + 1).padStart(2, "0")}</span>
                <h3 className="text-foreground font-semibold mt-3 mb-2">{p.step}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{p.detail}</p>
                {i < industry.pipeline.length - 1 && (
                  <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-border" />
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance + Outcomes */}
      <section className="py-20 lg:py-28 bg-card/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <p className={cn("text-sm font-mono uppercase tracking-wider mb-3", accent.text)}>Compliance & Regulation</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-6">What We Design Around</h2>
            <div className="flex flex-wrap gap-3">
              {industry.compliance.map((c) => (
                <span key={c} className="px-4 py-2 rounded-lg bg-secondary border border-border text-sm text-secondary-foreground">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <p className={cn("text-sm font-mono uppercase tracking-wider mb-3", accent.text)}>What You Get</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-6">Outcomes</h2>
            <div className="space-y-4">
              {industry.outcomes.map((o) => (
                <div key={o.label} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
                  <span className={cn("font-mono font-bold text-lg", accent.text)}>{o.metric}</span>
                  <span className="text-muted-foreground text-sm">{o.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related use cases */}
      {relatedUseCases.length > 0 && (
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-2xl mb-14">
              <p className={cn("text-sm font-mono uppercase tracking-wider mb-3", accent.text)}>In Practice</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground">Illustrative Use Cases</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedUseCases.map((u, i) => (
                <Reveal key={u.id} delay={i * 100} className="p-6 rounded-2xl bg-card border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{u.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{u.description}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className={cn("w-4 h-4", accent.text)} />
                    <span className="text-foreground font-medium">{u.outcome}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related capabilities */}
      <section className="py-20 lg:py-28 bg-card/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className={cn("text-sm font-mono uppercase tracking-wider mb-3", accent.text)}>Capabilities Applied Here</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">The Engineering Behind It</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedCaps.map((cap, i) => {
              const CapIcon = capabilityIcons[cap.icon] || FileCode2;
              return (
                <Reveal key={cap.id} delay={i * 100}>
                  <Link
                    to={`/capabilities#${cap.slug}`}
                    className="group block h-full p-6 rounded-2xl bg-card border border-border hover:border-primary/40 transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <CapIcon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-foreground font-semibold mb-2">{cap.title}</h3>
                    <p className="text-muted-foreground text-sm">{cap.short}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className={cn("relative overflow-hidden rounded-3xl border p-12 lg:p-16 text-center bg-card", accent.border)}>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Have a {industry.fullName} Data Challenge?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Let's talk through the architecture — no generic audit pitch, just the engineering your data actually needs.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12">
                Talk to an Architect
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
